// What pull.ts and push.ts run against. The engine builds one per start; tests build their own with a fake API, so
// both loops can be exercised without a browser, a network or React.

import type { Collection } from '@/contracts/collections';
import { idKey } from '@/contracts/uuid';
import type { Row } from '@/contracts/wire';
import { parseWireDate } from '@/contracts/wireDate';
import type { SyncApi } from './api';
import type { CachedRow, Replica } from './replica';

export interface SyncRuntime {
  api: SyncApi;
  replica: Replica;
  /** Lower-case company id (`p_company`). */
  companyId: string;
  /** The caller's `users` item ("mine" when revoked records take their dependents along); null if unknown. */
  profileId: string | null;
  /** Server-corrected clock for envelope times (clock.nowForEnvelope in the app). */
  now: () => Date;
  /** Once true, nothing may be written any more (sign-out, removal, company change). Checked after every await. */
  isStopped: () => boolean;
}

/** Map key of an item: `<collection>/<lower-case id>`. */
export function itemKey(collection: Collection, id: string): string {
  return `${collection}/${idKey(id)}`;
}

/** A live server row as the replica stores it; null for tombstones and rows without an object payload. */
export function toCachedRow(row: Row): CachedRow | null {
  if (row.deleted || !row.payload) return null;
  const cached: CachedRow = {
    id: row.id.toUpperCase(),
    payload: row.payload,
    version: row.version,
    serverSeq: row.server_seq,
    modifiedAt: row.modified_at,
    modifiedBy: row.modified_by,
  };
  if (row.file_path) cached.filePath = row.file_path;
  return cached;
}

/**
 * The recency rule of SyncReplica.apply: the local edit wins a both-sides change when its change time is later than
 * the server row's `modified_at` (an unparseable server time counts as 1970, like SupabaseSyncAPI.row).
 */
export function localIsNewer(localModifiedAt: string, serverModifiedAt: string): boolean {
  const local = parseWireDate(localModifiedAt)?.getTime() ?? 0;
  const server = parseWireDate(serverModifiedAt)?.getTime() ?? 0;
  return local > server;
}
